/**
 * t245u: one Members panel for a conversation - everyone who can read it, grouped by how they get access (t167u's
 * "Who can read this" and the old Members window, merged). "Added directly" carries Add people and Remove for those
 * allowed; groups from departments, groups and buildings are read-only here and edited in Channel settings →
 * Access (the "Manage access" link, managers only). A side panel on desktop, a bottom sheet on phones.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { CheckCheck, ChevronDown, ChevronRight, Circle, ListChecks, Search, UserMinus, UserPlus, Users } from 'lucide-react';
import { filterReaders, readersSummary, type Conversation, type ConversationReaders, type ReaderGroup, type ReaderPerson } from '@tradeoutlet/shared';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useWorkspaceId } from '@/lib/hooks';
import { usePermissionsAt } from '@/lib/permissions';
import { qk, queryClient } from '@/lib/queryClient';
import { useUi } from '@/lib/store';
import { Avatar, Button, Input, Modal, SheetClose, Spinner, useSidePanelFocus } from '@/components/ui';
import { HoverBubble } from '@/components/ui/HoverBubble';
import { confirmRemoval } from '@/lib/confirmRemove';
import { PersonButton } from '@/features/people/PersonDrawer';
import { PeoplePicker } from '@/features/people/PeoplePicker';
import { StaffListPicker } from '@/features/people/StaffListPicker';
import { useChatLimits } from '@/features/settings/PrefsPanels';
import { BottomSheet } from '@/mobile/BottomSheet';
import { useMobileLayout } from '@/mobile/layout';

/** The n/limit count shows once a channel has this many places left or fewer. */
export const NEAR_FULL = 10;
/** Search shows once the list is longer than this. */
export const SEARCH_FROM = 8;

/** Readers come from the API (the server's own read-access rule), refreshed when membership changes. */
export function useReaders(id: string, memberCount?: number) {
  return useQuery({ queryKey: ['conversation-readers', id, memberCount ?? 0], queryFn: () => api.conversationReaders(id), staleTime: 60_000 });
}

/** Header button: people icon + how many can read; hover for the breakdown, click for the panel. */
export function MembersButton({ id, memberCount, onOpen }: { id: string; memberCount?: number; onOpen: () => void }) {
  const q = useReaders(id, memberCount);
  const phone = useMobileLayout();
  const total = q.data?.total ?? memberCount;
  const button = (
    <Button variant="ghost" size="sm" onClick={onOpen} aria-label={`Members (${total ?? 0})`} data-testid="members-button">
      <Users className="h-4 w-4" />{total ?? ''}
    </Button>
  );
  // No hover on a phone: a tap would leave the bubble showing over the sheet.
  return q.data && !phone ? <HoverBubble bubble={readersSummary(q.data)} align="end">{button}</HoverBubble> : button;
}

export function MembersPanel({ conversation: conv, onClose, onManageAccess }: { conversation: Conversation; onClose: () => void; onManageAccess?: () => void }) {
  const phone = useMobileLayout();
  const q = useReaders(conv.id, conv.member_count ?? conv.members?.length);
  const title = 'Members';
  const panel = useRef<HTMLElement | null>(null);
  useSidePanelFocus(panel, !phone);
  const body = <PanelBody conv={conv} q={q} phone={phone} onManageAccess={onManageAccess} />;
  useEffect(() => {
    if (phone) return; // the sheet handles Escape itself
    const k = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [onClose, phone]);
  if (phone) return <BottomSheet open onClose={onClose} title={q.data ? `${title} · ${q.data.total}` : title} testId="members-sheet">{body}</BottomSheet>;
  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/20" onClick={onClose} aria-hidden />
      <aside ref={panel} tabIndex={-1} role="dialog" aria-modal="true" aria-label={title} data-testid="members-panel"
        className="fixed inset-y-0 right-0 z-50 flex w-96 flex-col outline-none border-l border-slate-200 bg-white shadow-2xl dark:border-slate-800 dark:bg-slate-900">
        <header className="flex h-14 shrink-0 items-center gap-2 border-b border-slate-200 px-4 dark:border-slate-800">
          <Users className="h-4 w-4 text-slate-500" />
          <h2 className="flex-1 font-semibold">{title}</h2>
          {q.data && <span className="text-sm tabular-nums text-slate-500" data-testid="members-total">{q.data.total}</span>}
          <SheetClose inline onClose={onClose} />
        </header>
        <div className="scroll-thin flex min-h-0 flex-1 flex-col overflow-y-auto">{body}</div>
      </aside>
    </>
  );
}

function PanelBody({ conv, q, phone, onManageAccess }: { conv: Conversation; q: ReturnType<typeof useReaders>; phone: boolean; onManageAccess?: () => void }) {
  const ws = useWorkspaceId();
  const { userId } = useAuth();
  const toast = useUi(s => s.toast);
  const [term, setTerm] = useState('');
  const [adding, setAdding] = useState<string[] | null>(null);
  const [staffList, setStaffList] = useState(false);
  const [busy, setBusy] = useState(false);
  const members = useMemo(() => conv.members ?? [], [conv.members]);
  const roles = useMemo(() => new Map(members.map(m => [m.user_id, m.role as string])), [members]);
  const limits = useChatLimits();
  const isRoom = conv.kind === 'room';
  const isChannel = conv.kind === 'channel' || isRoom;
  const limit = isRoom ? limits?.room_member_limit : conv.kind === 'channel' ? limits?.channel_member_limit : undefined;
  const memberCount = conv.member_count ?? members.length;
  const left = isChannel && limit ? Math.max(0, limit - memberCount) : undefined;
  const here = usePermissionsAt(`conversation:${conv.id}`).data;
  const canRemove = isRoom ? Boolean(conv.can_manage) && !conv.archived_at : conv.kind === 'channel' && (Boolean(conv.can_manage) || Boolean(here?.has('chat.channel.manage'))); // t723u: the server's Manage rule (owner, custom rule) as well
  const canAdd = isChannel && (isRoom ? Boolean(conv.can_add_members) : conv.is_member);
  const where = isRoom ? `“${conv.title ?? 'the ad-hoc meeting room'}”` : `#${conv.slug ?? 'channel'}`;
  const manageAccess = conv.kind === 'channel' && conv.can_manage && onManageAccess;
  const groups = useMemo(() => filterReaders(q.data?.groups ?? [], term), [q.data, term]);
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: qk.conversation(conv.id) });
    void queryClient.invalidateQueries({ queryKey: ['conversation-readers', conv.id] });
    if (ws) void queryClient.invalidateQueries({ queryKey: qk.conversations(ws) });
  };
  const add = async (ids: string[]) => {
    if (!ids.length) return;
    setBusy(true);
    try {
      const r = await api.addConversationMembers(conv.id, ids);
      if (r.added) toast(`Added ${r.added} ${r.added === 1 ? 'person' : 'people'}`, 'success');
      setAdding(null); setStaffList(false); refresh();
    } catch (e) { toast(errorMessage(e), 'error'); } finally { setBusy(false); }
  };
  const remove = async (p: ReaderPerson) => {
    if (!await confirmRemoval([p.display_name], isRoom ? 'this ad-hoc meeting room' : 'this channel')) return; // t259u
    try { await api.removeConversationMember(conv.id, p.user_id); toast(`Removed ${p.display_name}`, 'success'); refresh(); } catch (e) { toast(errorMessage(e), 'error'); }
  };
  const directActions = canAdd ? (
    <div className="flex shrink-0 items-center gap-1">
      {limit !== undefined && left !== undefined && left <= NEAR_FULL && <span className={clsx('px-1 text-xs tabular-nums', left === 0 ? 'font-medium text-red-700 dark:text-red-400' : 'text-slate-500')} data-testid="channel-capacity" title={`${memberCount} of ${limit} places used`}>{memberCount}/{limit}</span>}
      {!phone && <Button size="icon" variant="ghost" disabled={left === 0} onClick={() => setStaffList(true)} aria-label="Add from staff list" title="Add from staff list" data-testid="channel-staff-list"><ListChecks className="h-4 w-4" /></Button>}
      <Button size="sm" variant="ghost" disabled={left === 0} onClick={() => setAdding(a => (a ? null : []))} aria-expanded={adding !== null} data-testid="members-add"><UserPlus className="h-4 w-4" />Add</Button>
    </div>
  ) : null;
  return (
    <div className={clsx('flex flex-col', phone && 'min-h-[60vh] px-3')} data-testid="members-body">
      {(q.data?.total ?? 0) > SEARCH_FROM && (
        <div className={clsx('border-b border-slate-200 dark:border-slate-800', phone ? 'pb-2' : 'p-3')}>
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input noHint autoFocus={!phone} value={term} onChange={e => setTerm(e.target.value)} placeholder="Search by name or job title" aria-label="Search people" className="pl-8" data-testid="members-search" />
          </div>
        </div>
      )}
      {manageAccess && (
        <div className={clsx('flex justify-end', phone ? 'pt-1' : 'px-3 pt-2')}>
          <button type="button" onClick={onManageAccess} className="rounded text-xs font-medium text-slate-600 underline-offset-2 hover:text-slate-900 hover:underline dark:text-slate-300 dark:hover:text-white" data-testid="members-manage-access">Manage access</button>
        </div>
      )}
      <div className={clsx(!phone && 'p-2')}>
        {q.isLoading ? <div className="p-6 text-center"><Spinner /></div>
          : q.error ? <p className="p-4 text-sm text-rose-700">{errorMessage(q.error)}</p>
          : !groups.length && !(term.trim() === '' && directActions) ? <p className="p-4 text-center text-sm text-slate-500">Nobody matches.</p>
          : (groups.some(g => g.route === 'direct' || g.route === 'participants') || !directActions || term.trim() ? groups : [{ key: 'direct', route: 'direct' as const, label: 'Added directly', joinable: false, people: [] }, ...groups]).map(g => {
              const direct = g.route === 'direct' || g.route === 'participants';
              return (
                <Group key={g.key} g={g} searching={Boolean(term.trim())} data={q.data!} actions={direct ? directActions : null}
                  roles={direct ? roles : undefined} me={userId} onRemove={direct && canRemove ? p => void remove(p) : undefined}>
                  {direct && adding !== null && (
                    <div className="mb-2 space-y-2 px-2" data-testid="members-add-picker">
                      <PeoplePicker selected={adding} onChange={setAdding} exclude={members.map(m => m.user_id)} max={left} />
                      <div className="flex gap-2"><Button size="sm" loading={busy} onClick={() => void add(adding)} disabled={!adding.length} data-testid="members-add-confirm">Add {adding.length || ''}</Button><Button size="sm" variant="ghost" onClick={() => setAdding(null)}>Cancel</Button></div>
                    </div>
                  )}
                </Group>
              );
            })}
      </div>
      {staffList && (
        <Modal open wide="xl" onClose={() => setStaffList(false)} title={`Add to ${where} from the staff list`}>
          <StaffListPicker inIds={members.map(m => m.user_id)} notInLabel={`Not in ${where}`} max={left} busy={busy} onAdd={ids => add(ids)} />
        </Modal>
      )}
    </div>
  );
}

function Group({ g, searching, data, actions, roles, me, onRemove, children }: {
  g: ReaderGroup; searching: boolean; data: ConversationReaders; actions: ReactNode; roles?: Map<string, string>; me: string | null;
  onRemove?: (p: ReaderPerson) => void; children?: ReactNode;
}) {
  const [open, setOpen] = useState(!g.joinable || g.people.length <= 12);
  const shown = open || searching;
  const presence = useUi(s => s.presence);
  return (
    <section className="mb-1" data-testid="members-group" data-route={g.route}>
      <div className="flex items-center gap-1">
        <button type="button" onClick={() => setOpen(o => !o)} aria-expanded={shown}
          className="flex min-h-9 min-w-0 flex-1 items-center gap-1.5 rounded-md px-2 py-1.5 text-left text-xs font-semibold text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800">
          {shown ? <ChevronDown className="h-3.5 w-3.5 shrink-0" /> : <ChevronRight className="h-3.5 w-3.5 shrink-0" />}
          <span className="flex-1 truncate" data-testid="members-group-label">{g.label}</span>
          {g.joinable && <HoverBubble bubble={data.kind === 'channel' && !data.is_private ? 'Public channel: they can open it and join to read' : ''} align="end"><span className="text-[10px] font-medium text-slate-600 dark:text-slate-300">can join</span></HoverBubble>}
          <span className="tabular-nums" data-testid="members-group-count">{g.people.length}</span>
        </button>
        {actions}
      </div>
      {children}
      {shown && (
        <ul>
          {g.people.map(p => {
            const role = roles?.get(p.user_id);
            return (
              <li key={p.user_id} className="flex min-h-11 items-center gap-2.5 rounded-md px-2 py-1 hover:bg-slate-50 dark:hover:bg-slate-800/60" data-testid="members-person">
                <PersonButton userId={p.user_id} name={p.display_name} tabIndex={-1} className="shrink-0 rounded-md"><Avatar size={28} name={p.display_name} id={p.user_id} presence={roles ? presence[p.user_id] ?? 'offline' : undefined} /></PersonButton>
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-1.5">
                    <PersonButton userId={p.user_id} name={p.display_name} className="min-w-0 truncate rounded text-left text-sm font-medium hover:underline">{p.display_name}</PersonButton>
                    {p.user_id === me && <span className="shrink-0 text-xs text-slate-500">(you)</span>}
                    {role && role !== 'member' && <span className="shrink-0 text-[10px] font-medium text-slate-600 dark:text-slate-300" data-testid="members-role">{role[0].toUpperCase() + role.slice(1)}</span>}
                  </div>
                  {(p.job_title || p.also_via.length > 0) && (
                    <div className="truncate text-xs text-slate-500">
                      {p.job_title}{p.job_title && p.also_via.length ? ' · ' : ''}{p.also_via.length > 0 && <span data-testid="members-also">also via {p.also_via.join(', ')}</span>}
                    </div>
                  )}
                </div>
                {p.read_latest !== null && (
                  <HoverBubble bubble={p.read_latest ? 'Has read the latest message' : "Hasn't read the latest message yet"} align="end">
                    <span className={clsx('shrink-0', p.read_latest ? 'text-slate-900 dark:text-white' : 'text-slate-300 dark:text-slate-600')} data-testid="members-read" data-read={p.read_latest}>
                      {p.read_latest ? <CheckCheck className="h-4 w-4" aria-label="Read" /> : <Circle className="h-3.5 w-3.5" aria-label="Not read yet" />}
                    </span>
                  </HoverBubble>
                )}
                {onRemove && p.user_id !== me && (
                  <Button size="icon" variant="ghost" title="Remove" aria-label={`Remove ${p.display_name}`} onClick={() => onRemove(p)} data-testid="members-remove"><UserMinus className="h-4 w-4" /></Button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
